// src/pages/ProjectPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { REGIONES } from "src/features/showcase/data/regions";
import { House } from "lucide-react";
import { SplashScreen } from "src/components/SplashScreen/SplashScreen";
import { AnimatePresence, motion } from "framer-motion";

const MIN_SPLASH_MS = 3000;
const PANEL_ANIM_MS = 1100; // tu animación dura 1.1s

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const region = REGIONES.find((r) => r.id === Number(id));
  const regionName = region?.name ?? `Región ${id}`;

  const [showSplash, setShowSplash] = useState(true);
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);

  const splashStartRef = useRef(Date.now());

  useEffect(() => {
    splashStartRef.current = Date.now();
    setShowSplash(true);
    setIframeSrc(null);

    // ⏳ retrasar carga del iframe hasta que termine la animación del panel
    const t = setTimeout(() => {
      setIframeSrc(`https://app.rdx.social/project/${id}`);
      // setIframeSrc(`http://localhost:5173/project/${id}`);
    }, PANEL_ANIM_MS);

    return () => clearTimeout(t);
  }, [id]);

  const handleIframeLoad = () => {
    const elapsed = Date.now() - splashStartRef.current;
    const remaining = MIN_SPLASH_MS - elapsed;

    if (remaining <= 0) {
      setShowSplash(false);
    } else {
      setTimeout(() => setShowSplash(false), remaining);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { delay: 0.5 } }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-20 flex flex-col pointer-events-none"
    >
      {/* Botón Home */}
      <button
        onClick={() => navigate("/")}
        className="
          border-gradient cursor-pointer pointer-events-auto absolute top-4 left-4 z-30
          w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center
          rounded-full bg-black hover:bg-black/60
          backdrop-blur-md transition-colors text-white text-xl
        "
      >
        <House strokeWidth={1.25} />
      </button>

      {/* Panel */}
      <div className="relative flex flex-col flex-1 w-full max-w-screen-xl mx-auto p-4 pt-16 pb-16 sm:p-10">
        <motion.div
          initial={{
            opacity: 0,
            y: 28, // ✔ NO lo cambié
            scale: 0.985, // ✔ no lo cambié
            filter: "blur(10px)", // ✔ intacto
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            filter: "blur(0px)",
          }}
          transition={{
            duration: 1.1, // ✔ igual
            ease: "easeInOut", // ✔ igual
          }}
          className="
            pointer-events-auto relative flex-1 rounded-3xl border-gradient overflow-hidden
            shadow-[0_0_80px_20px_rgba(0,0,0,0.45)]
          "
        >
          {/* Iframe solo se carga cuando corresponde */}
          {iframeSrc && (
            <iframe
              src={iframeSrc}
              title={regionName}
              className="absolute inset-0 w-full h-full"
              onLoad={handleIframeLoad}
              allow="accelerometer; gyroscope; magnetometer; fullscreen; xr-spatial-tracking; autoplay; encrypted-media"
              allowFullScreen
            />
          )}

          {/* Loader dentro del panel */}
          <AnimatePresence mode="wait">
            {showSplash && region?.cover_url && (
              <SplashScreen key="splash" backgroundUrl={region.cover_url} />
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}
